/**
 * vehicleDraftDB.ts
 * Helper de IndexedDB para persistir el borrador completo del formulario
 * de registro de vehículo, incluyendo archivos binarios (File / Blob).
 *
 * IndexedDB soporta objetos estructurados (Structured Clone Algorithm),
 * lo que incluye File, Blob, ArrayBuffer, etc.
 */

const DB_NAME = 'FletyVehicleDraft';
const DB_VERSION = 1;
const STORE_NAME = 'draft';

/**
 * Genera un ID único de draft asociado al usuario
 * Esto previene que diferentes usuarios compartan el mismo draft
 */
const getDraftId = (userId: string): string => `vehicle_create_${userId}`;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e: IDBVersionChangeEvent) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Guarda el objeto `data` en IndexedDB bajo la clave del draft asociada al usuario */
export async function saveDraftToDB(data: object, userId: string): Promise<void> {
  if (!userId) {
    console.warn('[vehicleDraftDB] No se puede guardar draft sin userId');
    return;
  }
  try {
    const db = await openDB();
    const draftId = getDraftId(userId);
    console.log(`[vehicleDraftDB] 💾 Guardando draft para usuario: ${userId}`);
    console.log(`[vehicleDraftDB] 🔑 Draft ID: ${draftId}`);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({ id: draftId, userId, ...data });
      tx.oncomplete = () => {
        console.log(`[vehicleDraftDB] ✅ Draft guardado exitosamente`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('[vehicleDraftDB] Error guardando draft:', e);
  }
}

/** Lee el draft guardado para el usuario específico, o `null` si no existe */
export async function loadDraftFromDB(userId: string): Promise<any | null> {
  if (!userId) {
    console.warn('[vehicleDraftDB] No se puede cargar draft sin userId');
    return null;
  }
  try {
    const db = await openDB();
    const draftId = getDraftId(userId);
    console.log(`[vehicleDraftDB] 📖 Intentando cargar draft para usuario: ${userId}`);
    console.log(`[vehicleDraftDB] 🔑 Buscando Draft ID: ${draftId}`);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(draftId);
      req.onsuccess = () => {
        const result = req.result ?? null;
        console.log(`[vehicleDraftDB] 📦 Resultado de búsqueda:`, result ? '✅ Draft encontrado' : '❌ No hay draft');
        
        // Validación extra: verificar que el userId coincida
        if (result && result.userId !== userId) {
          console.warn(`[vehicleDraftDB] ⚠️ Draft no pertenece al usuario actual (esperado: ${userId}, encontrado: ${result.userId}), ignorando`);
          resolve(null);
        } else {
          if (result) {
            console.log(`[vehicleDraftDB] ✅ Draft cargado exitosamente para usuario ${userId}`);
            console.log(`[vehicleDraftDB] 📊 Step guardado: ${result.step}`);
          }
          resolve(result);
        }
      };
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn('[vehicleDraftDB] Error leyendo draft:', e);
    return null;
  }
}

/** Elimina el draft guardado del usuario específico */
export async function clearDraftFromDB(userId: string): Promise<void> {
  if (!userId) {
    console.warn('[vehicleDraftDB] No se puede limpiar draft sin userId');
    return;
  }
  try {
    const db = await openDB();
    const draftId = getDraftId(userId);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(draftId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('[vehicleDraftDB] Error limpiando draft:', e);
  }
}

/**
 * Limpia el draft del usuario al cerrar sesión
 * Esta función debe llamarse desde el logout para evitar que
 * el draft de un usuario se muestre a otro usuario
 */
export async function clearAllDraftsOnLogout(userId: string): Promise<void> {
  if (!userId) {
    console.warn('[vehicleDraftDB] No se puede limpiar drafts en logout sin userId');
    return;
  }
  console.log(`[vehicleDraftDB] Limpiando drafts del usuario ${userId} al cerrar sesión`);
  await clearDraftFromDB(userId);
}
